import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelector } from '../../utility/utils';

class Texts extends BaseObject { }

class ClassList extends BaseObject { }

class Selectors extends BaseObject {
    mainMenu = '.header-main-menu';
    secondaryMenu = ".secondary-menu__list";
    navItem = '.nav-item';
    navLink = '.nav-link';
    cloneLink = '.nav-item:not(.no-clone) > a';
    link = 'a';
    cloneSubLink = `.menu-item:not(.no-clone) > a`;
    sidebarWrapper = '.header-sidebar';
    sidebarMiddleWrapper = '.header-sidebar__middle .header-sidebar__nav';
    tertiary = '.sublist--tertiary';
}

class Elements extends BaseElements<Selectors> {
    mainMenu: HTMLElement;
    secondaryMenu: HTMLElement;
    navItem: HTMLElement;
    sidebarWrapper: HTMLElement;
    sidebarMiddleWrapper: HTMLElement;
    secondaryListParent: HTMLElement;


    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.initElements();
    }

    initElements() {
        this.mainMenu = querySelector(this.selectors.mainMenu, this.wrapper);
        this.secondaryMenu = querySelector(this.selectors.secondaryMenu);
        this.navItem = querySelector(this.selectors.navItem, this.wrapper);
        this.sidebarWrapper = querySelector(this.selectors.sidebarWrapper);
        this.sidebarMiddleWrapper = querySelector(this.selectors.sidebarMiddleWrapper, this.sidebarWrapper);
        this.secondaryListParent = this.sidebarMiddleWrapper;
    }

    constructor(readonly selectors: Selectors) {
        super(selectors, false);
    }
}

interface HeaderItemsCloner extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class HeaderItemsCloner {
    constructor(readonly wrapper: HTMLElement) { }

    get classes() {
        return {
            sublist: "list-unstyled sublist pl-16 mt-8",
            sublistTertiary: "list-unstyled sublist--tertiary",
            menuLink: "pl-0",
            subMenuLink: "pl-0"
        }
    }

    get subList() {
        return `<ul class="${this.classes.sublist}"></ul>`;
    }

    get tertiaryList() {
        return `<ul class="${this.classes.sublistTertiary}"></ul>`;
    }

    initialize() {
        this.setElements();
        this.cloneHeaderMenuItems();
        if (querySelector(".secondary-menu__list")) {
            this.cloneSecondaryItems();
        }
    }

    setElements(): void {
        this.elements.setWrapper(this.wrapper);
    }

    cloneHeaderMenuItems(): void {
        const items: NodeListOf<Element> | undefined[] = this.elements.mainMenu?.querySelectorAll(`${this.selectors.navItem}:not(.no-clone)`);
        if (items) {
            this.addListToSidebar(items);
        }
    }

    addListToSidebar(list: NodeListOf<Element> | any[]): void {
        list.forEach((item: HTMLElement) => {
            this.elements.sidebarMiddleWrapper?.insertAdjacentHTML('beforeend', this.itemHTML(item));
        });
    }

    itemHTML(item: HTMLElement): string {
        const listItemClasses = item.classList.contains('nav-link')
            ? 'nav-item--primary font-weight-bolder'
            : 'nav-item--secondary font-weight-bold';
        const extraClasses = item.dataset.classes;

        if (this.isDropdown(item)) {
            const random = Math.floor(Math.random() * 100);
            const toggler = item.querySelector('.dropdown-toggle')
            const target = `#dropdown-${random}`;
            const dropdownItems = item.querySelectorAll('.dropdown-item')
            const dropdownItemsHTML = this.getDropdownItemsHTML(dropdownItems);
            return `
                    <li class="nav-item ${listItemClasses} ${extraClasses ? extraClasses : ""}">
                        <a href="${target}" class="dropdown-toggle ${this.classes.menuLink}" data-toggle="collapse" data-target="${target}" aria-expanded="false" 
                        aria-controls="dropdown-${random}">
                            ${toggler.textContent}
                        </a>
                        <ul class="dropdown collapse list-unstyled" id="dropdown-${random}">
                            ${dropdownItemsHTML}
                        </ul>
                    </li>`
        }
        else {
            const navLink = item.querySelector('.nav-link');
            const href = navLink.getAttribute('href');
            const target = navLink.getAttribute('target');
            return `
                    <li class="nav-item ${listItemClasses} ${extraClasses ? extraClasses : ""}">
                        <a class="nav-link ${this.classes.menuLink}" href="${href}" ${target ? `target=${target}` : ""}>
                            ${item.textContent}
                        </a>
                    </li>`;
        }
    }

    getDropdownItemsHTML(items: NodeListOf<Element>) {
        const dropdownMenuItemsContent = [];
        items.forEach(item => {
            const target = item.getAttribute('target');
            dropdownMenuItemsContent.push(`
            <li class="nav-item nav-item--secondary">
                <a class="nav-link ${this.classes.menuLink}" href=${item.getAttribute("href")} ${target ? `target=${target}` : ""}>
                    ${item.textContent}
                </a>
            </li>`)
        });
        return dropdownMenuItemsContent.join(" ");
    }

    isDropdown(item: HTMLElement): boolean {
        return item.children[0].classList.contains("dropdown");
    }

    cloneSecondaryItems(): void {
        const parentEl = this.getParentEl();
        if (!parentEl) {
            this.addSubItemsToSidebar(this.elements.secondaryListParent);
            return;
        }

        const subList = parentEl.children[parentEl.children.length - 1];
        this.addSubItemsToSidebar(subList);
    }

    getParentEl(): Element | undefined {
        if (!this.elements.sidebarMiddleWrapper) {
            return;
        }
        const parent = this.elements.secondaryMenu?.dataset.parent;
        if (!parent) return;
        const parentEl = Array.from(this.elements.sidebarMiddleWrapper.querySelectorAll(this.selectors.navItem)).find(
            item => {
                return item.textContent.trim().toLowerCase() === parent.trim().toLowerCase();
            }
        );
        if (!parentEl) return;
        parentEl.classList.add("active");
        parentEl.querySelector(this.selectors.link)?.classList.add("active");
        parentEl.querySelector(this.selectors.link)?.classList.remove("pl-0");
        parentEl.insertAdjacentHTML('beforeend', this.subList);
        return parentEl;
    }

    addSubItemsToSidebar(parent: Element): void {
        const list: NodeListOf<HTMLElement> = this.elements.secondaryMenu?.querySelectorAll(this.selectors.cloneLink);
        list.forEach((item: HTMLElement) => {
            if (item.classList.contains('no-clone')) return;
            parent.insertAdjacentHTML('beforeend', this.subListItemHTML(item, this.classes.subMenuLink));
            if (item.nextElementSibling && item.nextElementSibling.classList.contains("dropdown-menu")) {
                this.addTertiaryItems(parent, item);
            }
        });
    }

    addTertiaryItems(parent: Element, item: HTMLElement) {
        const children = parent.children;
        let lastChild: Element = children[children.length - 1];
        if (!lastChild) {
            return;
        }
        lastChild.insertAdjacentHTML('beforeend', this.tertiaryList);
        const sublistTertiary: HTMLElement = lastChild.querySelector(this.selectors.tertiary);
        item.nextElementSibling.querySelectorAll(this.selectors.cloneSubLink).forEach(subItem => {
            sublistTertiary.insertAdjacentHTML('beforeend', this.subListItemHTML(subItem));
        });
    }

    subListItemHTML(item: Element, classes?: string): string {
        const subItemClasses = `font-weight-normal nav-item--subitem ${item.closest(this.selectors.navItem).nextElementSibling ? '' : 'pb-0'
            }`;
        const activeClass = item.classList.contains('active') ? 'text-primary' : '';
        const extraClasses = item.parentElement.dataset.classes;
        const target = item.getAttribute('target');
        return `
            <li class="nav-item ${subItemClasses} ${extraClasses}">
                <a class="nav-link ${activeClass} ${classes ? classes : ""}" href="${item.getAttribute('href')}" ${target ? `target=${target}` : ""}>
                    ${item.textContent}
                </a>
            </li>`;
    }
}

export default HeaderItemsCloner;

export {
    Elements as HeaderItemsClonerElements,
    Selectors as HeaderItemsClonerSelectors,
    ClassList as HeaderItemsClonerClassList,
    Texts as HeaderItemsClonerTexts
}